import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Grid2 as Grid } from "@mui/material";
import DeleteIcon from "../../assets/deleteIcon.png";
import OutletDialog from "../../common/outletDialog/outletDialog";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import ConfirmationDialog from "../../common/confirmationDialog/ConfirmationDialog";
import DataTable from "../../common/DataTable";
import { defaultSalesRepData } from "../../constants/defaultValues";
import { SalesRep, Outlet } from "../../constants/types";
import { salesRepDetails } from "../../schemas/salesRepDetails";
import ControlledTextField from "../../common/ControlledTextField";
import { api } from "../../api/api";
import { endpoints } from "../../constants/endpoints";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  addUser,
  removeUser,
  setUsersByRole,
} from "../../redux/features/slices/userSlice";
import {
  Title,
  Label,
  PaginationCaption,
  PaginationControlBox,
  StyledCard,
  NameStyle,
} from "../userManagement/userManagementStyles";
import moment from "moment";
import Toast from "../../common/Toast";
import Pagination from "@mui/material/Pagination";
import {
  checkDetailsStyles,
  OuterContainer,
  FullWidthBox,
  AddSectionBox,
  AddSectionGrid,
  ListSectionBox,
  outlinedButtonStyles,
  containedButtonStyles,
} from "./SalesRepManagementScreen.styles";
import { fetchSalesReps } from "../../api/customerAPI";
import { fetchAssignedOutlets } from "../../api/userAPI";
interface ApiUser {
  id: string;
  name: string;
  email?: string;
  createdAt?: string;
  phone_number?: string;
  role: string;
}

const SalesRepManagementScreen: React.FC = () => {
  const orgId = localStorage.getItem("orgId");
  const dispatch = useAppDispatch();
  const users = useAppSelector((state) => state.users.users);
  const salesReps = users.filter((user) => user.role === "maltora-sales-rep");

  const { control, handleSubmit, watch, reset } = useForm({
    defaultValues: defaultSalesRepData,
    resolver: yupResolver(salesRepDetails),
    mode: "onSubmit",
  });

  const fieldValues = watch();

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [salesRepToDelete, setSalesRepToDelete] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const [showOutletPopup, setShowOutletPopup] = useState(false);
  const [selectedSalesRepOutlets, setSelectedSalesRepOutlets] = useState<
    Outlet[]
  >([]);

  const [error, setError] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const navigate = useNavigate();

  const handleGetSalesReps = useCallback(async () => {
    setToastMessage("Failed to fetch sales representatives");
    setError(true);
    try {
      const salesRepsData = await fetchSalesReps();
      if (salesRepsData) {
        const transformedData = salesRepsData.map((user: ApiUser) => ({
          ...user,
          firstName: user.name?.split(" ")[0] || "",
          lastName: user.name?.split(" ")[1] || "",
          createdDate: user.createdAt
            ? moment(user.createdAt).format("DD MMMM YY hh:mm:ss A")
            : "",
          email: user.email || "",
          phone_number: user.phone_number || "",
        }));
        dispatch(
          setUsersByRole({ users: transformedData, role: "maltora-sales-rep" })
        );
      }
      setError(false);
      setToastMessage("Sales representatives fetched successfully");
      setShowToast(false);
    } catch (error) {
      console.log("Failed to get data, Reason", error);
      setShowToast(true);
    }
  }, [dispatch, setError, setShowToast, setToastMessage]);

  const handleAddSalesRep = async () => {
    setToastMessage("Failed to add sales representative");
    setError(true);

    const values = {
      name:
        fieldValues.firstName +
        (fieldValues.lastName ? " " + fieldValues.lastName : ""),
      email: fieldValues.email,
      phone_number: "0",
      country_code: "USA",
      role: "maltora-sales-rep",
    };

    try {
      const response = await api.post(endpoints.addUser, values, {
        headers: {
          orgId: localStorage.getItem("organizationId"),
        },
      });

      if (response.status === 201) {
        const newSalesRep = {
          id: response.data.data.id,
          firstName: fieldValues.firstName,
          lastName: fieldValues.lastName || "",
          email: fieldValues.email,
          createdDate: moment(response.data.data.createdAt).format(
            "DD MMMM YY hh:mm:ss A"
          ),
          phone_number: response.data.data.phone_number,
          role: "maltora-sales-rep",
        };
        dispatch(addUser(newSalesRep));
        setError(false);
        setToastMessage("Sales representative added successfully");
        reset();
        setShowToast(false);
      } else {
        setShowToast(true);
      }
    } catch (error: unknown) {
      if (error && typeof error === "object" && "response" in error) {
        const apiError = error as {
          response: { data: { error: { message: string } } };
        };
        if (apiError.response?.data?.error?.message === "User Exists") {
          setToastMessage("The email ID already exists.");
        }
      }
      console.error(error);
      setShowToast(true);
    }
  };

  const handleDeleteSalesRep = async (id: string) => {
    setSalesRepToDelete(id);
    setShowDeleteDialog(true);
  };

  const confirmDeleteSalesRep = async () => {
    setError(true);
    setToastMessage("Failed to delete sales representative");

    setShowDeleteDialog(false);
    const salesRepUser = users.find((user) => user.id === salesRepToDelete);
    const { email, id } = salesRepUser || {};

    try {
      const response = await api.delete(endpoints.deleteUser, {
        data: {
          id: id,
          email: email,
        },
        headers: {
          orgId: localStorage.getItem("organizationId"),
        },
      });
      if (response.status === 200) {
        if (id) {
          dispatch(removeUser(id));
        }
        setError(false);
        setToastMessage("Sales representative deleted successfully");
        setShowToast(false);
      } else {
        setShowToast(true);
      }
    } catch (err) {
      console.error("Failed to delete:", err);
      setShowToast(true);
    }
    setSalesRepToDelete(null);
  };

  const handleViewOutlets = async (salesRep: SalesRep) => {
    setShowOutletPopup(true);

    // If no email available, show empty state
    if (!salesRep.email) {
      setSelectedSalesRepOutlets([]);
      return;
    }

    try {
      const response = await fetchAssignedOutlets({
        userEmail: salesRep.email,
        page: 1,
        limit: 100,
      });
      if (
        response &&
        response.data &&
        response.data.data &&
        Array.isArray(response.data.data.assignedOutlets)
      ) {
        setSelectedSalesRepOutlets(response.data.data.assignedOutlets);
      } else {
        setSelectedSalesRepOutlets([]);
      }
    } catch (error) {
      console.error("Failed to fetch assigned outlets:", error);
      setSelectedSalesRepOutlets([]);
    }
  };

  const handleCloseOutletPopup = () => {
    setShowOutletPopup(false);
    setSelectedSalesRepOutlets([]);
  };

  const handleAssignOutlets = () => {
    navigate(`/maltora-seller/sales-rep-management/assign-outlets`);
  };

  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, salesReps.length);
  const totalRows = salesReps.length;

  const paginatedSalesReps = salesReps.slice(startIndex, endIndex);

  const columns: {
    label: string;
    field: string;
    align?: "left" | "center" | "right";
    renderCell?: (row: SalesRep) => JSX.Element;
  }[] = [
    {
      label: "Name",
      field: "name",
      align: "center",
      renderCell: (row: SalesRep) => (
        <NameStyle>{`${row.firstName} ${row.lastName || ""}`}</NameStyle>
      ),
    },
    {
      label: "Created Date",
      field: "createdDate",
      align: "center",
    },
    {
      label: "Email",
      field: "email",
      align: "center",
    },
    {
      label: "Assigned Outlets",
      field: "assignedOutlets",
      align: "center",
      renderCell: (row: SalesRep) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleViewOutlets(row);
          }}
          style={checkDetailsStyles}
        >
          Check
        </button>
      ),
    },
    {
      label: "Delete",
      field: "delete",
      align: "center",
      renderCell: (row: SalesRep) => (
        <img
          src={DeleteIcon}
          onClick={() => row.id && handleDeleteSalesRep(row.id)}
          aria-label="Delete"
          style={{ cursor: "pointer", width: "16px", height: "16px" }}
        />
      ),
    },
  ];

  useEffect(() => {
    handleGetSalesReps();
  }, [handleGetSalesReps]);

  return (
    <>
      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
      />

      <OuterContainer>
        <FullWidthBox>
          <StyledCard>
            <AddSectionBox>
              <Title>Add New Sales Representative</Title>
              <AddSectionGrid container spacing={2}>
                {/* First Name */}
                <Grid size={[12, 4]}>
                  <Label>First Name*</Label>
                  <ControlledTextField
                    control={control}
                    name="firstName"
                    placeholder=""
                  />
                </Grid>
                {/* Last Name */}
                <Grid size={[12, 4]}>
                  <Label>Last Name</Label>
                  <ControlledTextField
                    control={control}
                    name="lastName"
                    placeholder=""
                  />
                </Grid>
                {/* Email */}
                <Grid size={[12, 4]}>
                  <Label>Email ID*</Label>
                  <ControlledTextField
                    control={control}
                    name="email"
                    placeholder=""
                  />
                </Grid>
              </AddSectionGrid>
              <Grid container justifyContent="flex-end" spacing={2} mt="40px">
                <Grid size={{ xs: 12, md: 2.5 }}>
                  <Button
                    fullWidth
                    sx={outlinedButtonStyles}
                    onClick={() => reset()}
                  >
                    Reset
                  </Button>
                </Grid>

                <Grid size={{ xs: 12, md: 2.5 }}>
                  <Button
                    fullWidth
                    sx={outlinedButtonStyles}
                    onClick={handleSubmit(handleAddSalesRep)}
                  >
                    Create
                  </Button>
                </Grid>

                <Grid size={{ xs: 12, md: 2.5 }}>
                  <Button
                    fullWidth
                    sx={containedButtonStyles}
                    onClick={handleAssignOutlets}
                  >
                    Assign Outlets
                  </Button>
                </Grid>
              </Grid>
            </AddSectionBox>
            <ListSectionBox>
              <Title>List Of Sales Representatives</Title>
            </ListSectionBox>
            <Box>
              <DataTable
                columns={columns}
                data={paginatedSalesReps}
                emptyStateMessage="No sales representatives found."
                rowsPerPage={5}
              />
              <PaginationControlBox>
                <PaginationCaption variant="subtitle2">
                  Showing data {startIndex + 1} to {endIndex} of {totalRows}{" "}
                  entries
                </PaginationCaption>
                <Pagination
                  count={Math.ceil(salesReps.length / rowsPerPage)}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  shape="rounded"
                  variant="outlined"
                />
              </PaginationControlBox>
            </Box>
          </StyledCard>
        </FullWidthBox>
      </OuterContainer>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        show={showDeleteDialog}
        setShow={setShowDeleteDialog}
        title="Are you sure you want to delete this sales representative?"
        buttonTitle="Yes, I Am Sure"
        buttonAction={confirmDeleteSalesRep}
      />

      {/* Outlet Dialog */}
      <OutletDialog
        open={showOutletPopup}
        onClose={handleCloseOutletPopup}
        rows={selectedSalesRepOutlets.map((o) => ({
          outletName: o.name,
          email: o.email,
        }))}
        maxWidth="sm"
        emptyMessage="No outlets assigned to this sales representative."
      />
    </>
  );
};

export default SalesRepManagementScreen;
